import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faClock, faTrash, faPencil, faRepeat, faChevronUp, faChevronDown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import prayerService from '../../api/prayerService';
import { sortItemsByTimeParts } from '../../utils/timeUtils';
import { useSnackbar } from '../../contexts/SnackbarContext';

const DayAgendaPanel = ({ 
  isOpen, 
  onClose, 
  groupId, 
  selectedDate,
  dayName,
  allPrayerTimes,
  onPrayerTimesUpdate,
  holidays = []
}) => {
  const { showSnackbar } = useSnackbar();
  const [agendaItems, setAgendaItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, index: null, item: null });
  const [deleteOption, setDeleteOption] = useState('thisDay'); // 'thisDay' or 'allDays'
  const [saveConfirm, setSaveConfirm] = useState({ show: false, index: null }); // Confirmation for zmanim rule saves
  const [manuallyReordered, setManuallyReordered] = useState(false); // Track if user manually reordered
  const [hasChanges, setHasChanges] = useState(false); // Track if any changes were made
  const hasLoadedRef = useRef(false); // Track if we've loaded initial data
  const lastLoadedDateRef = useRef(null); // Track which date we loaded
  const [zmanimPreview, setZmanimPreview] = useState({}); // Store preview times by item index
  const [calculatingZmanim, setCalculatingZmanim] = useState(null); // Track which item is calculating (by index)
  
  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Convert 12-hour time string to separate hours, minutes, and period for input
  const parseTime12Hour = (time12h) => {
    if (!time12h) return { hour: '', minute: '', period: 'AM' };
    try {
      const match = time12h.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) return { hour: '', minute: '', period: 'AM' };
      return {
        hour: match[1].padStart(2, '0'),
        minute: match[2],
        period: match[3].toUpperCase()
      };
    } catch (error) {
      return { hour: '', minute: '', period: 'AM' };
    }
  };

  // Format time parts back to 12-hour string
  const formatTime12Hour = (hour, minute, period) => {
    if (!hour || !minute) return null;
    const h = parseInt(hour, 10);
    const displayHour = h === 0 ? 12 : h;
    return `${displayHour}:${minute} ${period}`;
  };

  // Map zmanim fields from API response to component state
  const mapZmanimFields = (item) => {
    let zmanimLocationInput = '';
    if (item.zmanim_location_zipcode) {
      zmanimLocationInput = item.zmanim_location_zipcode;
    } else if (item.zmanim_location_geonameid) {
      zmanimLocationInput = String(item.zmanim_location_geonameid);
    } else if (item.zmanim_location_lat && item.zmanim_location_lng) {
      zmanimLocationInput = `${item.zmanim_location_lat},${item.zmanim_location_lng}`;
    }

    return {
      useZmanimRule: item.zmanim_rule_enabled || false,
      zmanimType: item.zmanim_type || 'sunset',
      zmanimOffsetMinutes: item.zmanim_offset_minutes || 0,
      zmanimOffsetDirection: item.zmanim_offset_direction || 'before',
      zmanimLocationInput: zmanimLocationInput,
      zmanimCityName: item.zmanim_city_name || '',
      appliedZmanimRule: item.applied_zmanim_rule || ''
    };
  };

  // Format date for display
  const formatDisplayDate = () => {
    const d = new Date(selectedDate);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return d.toLocaleDateString('en-US', options);
  };

  // Move item up in the list
  const handleMoveUp = async (index) => {
    try {
      console.log('⬆️ MOVE UP CLICKED - index:', index, 'total items:', agendaItems.length);
      
      if (index === 0) {
        console.log('⬆️ Already at top, ignoring');
        return; // Already at top
      }
      
      console.log('⬆️ Moving item up from index', index);
      setManuallyReordered(true); // Mark that user manually reordered
      
      const items = [...agendaItems];
      // Swap with item above
      [items[index - 1], items[index]] = [items[index], items[index - 1]];
      
      console.log('⬆️ New order:', items.map((i, idx) => `${idx}: ${i.title}`));
      
      // Update state immediately
      setAgendaItems(items);
      
      // Save to database
      console.log('⬆️ About to call saveOrder...');
      await saveOrder(items);
      console.log('⬆️ saveOrder completed');
    } catch (error) {
      console.error('⬆️ ERROR in handleMoveUp:', error);
      setError('Failed to move item up: ' + error.message);
    }
  };

  // Move item down in the list
  const handleMoveDown = async (index) => {
    try {
      console.log('⬇️ MOVE DOWN CLICKED - index:', index, 'total items:', agendaItems.length);
      
      if (index === agendaItems.length - 1) {
        console.log('⬇️ Already at bottom, ignoring');
        return; // Already at bottom
      }
      
      console.log('⬇️ Moving item down from index', index);
      setManuallyReordered(true); // Mark that user manually reordered
      
      const items = [...agendaItems];
      // Swap with item below
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
      
      console.log('⬇️ New order:', items.map((i, idx) => `${idx}: ${i.title}`));
      
      // Update state immediately
      setAgendaItems(items);
      
      // Save to database
      console.log('⬇️ About to call saveOrder...');
      await saveOrder(items);
      console.log('⬇️ saveOrder completed');
    } catch (error) {
      console.error('⬇️ ERROR in handleMoveDown:', error);
      setError('Failed to move item down: ' + error.message);
    }
  };

  // Save the order to database
  const saveOrder = async (items) => {
    try {
      setIsLoading(true);
      
      // Create items array with updated display_order
      const itemsWithOrder = items.map((item, index) => ({
        id: item.id,
        display_order: index
      }));

      // Determine if these are recurring items or date-specific overrides
      const isDateSpecific = items.length > 0 && items[0].isDateSpecific;
      
      console.log('💾 Saving order:', {
        isDateSpecific,
        itemCount: itemsWithOrder.length,
        items: itemsWithOrder
      });
      
      if (isDateSpecific) {
        // Update order for date-specific overrides
        const dateStr = new Date(selectedDate).toISOString().split('T')[0];
        await prayerService.updateAgendaOverridesOrder(groupId, dateStr, itemsWithOrder);
        console.log('✅ Saved display order for date-specific overrides');
      } else {
        // Update order for recurring items
        await prayerService.updateAgendaItemsOrder(groupId, itemsWithOrder);
        console.log('✅ Saved display order for recurring items');
      }

      // DON'T call onPrayerTimesUpdate - it causes a force refresh which resets the order
      // The order will be reflected when the user closes and reopens the panel or navigates
      console.log('✅ Order saved successfully - skipping force refresh to prevent reset');
    } catch (error) {
      console.error('❌ Error saving display order:', error);
      setError('Failed to save order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };



  // Load existing agenda items - ONLY on initial open or date change
  useEffect(() => {
    console.log('🔍 Panel useEffect triggered:', {
      isOpen,
      hasLoaded: hasLoadedRef.current,
      lastLoadedDate: lastLoadedDateRef.current,
      currentDate: selectedDate,
      itemsCount: agendaItems.length,
      manuallyReordered
    });
    
    if (isOpen && groupId && selectedDate) {
      // Check if date changed - if so, reset and reload
      if (lastLoadedDateRef.current !== selectedDate) {
        console.log('📅 Date changed, resetting load flag');
        hasLoadedRef.current = false;
        lastLoadedDateRef.current = selectedDate;
        setManuallyReordered(false); // Reset manual reorder flag on date change
      }
      
      // Only load once when panel opens or date changes, AND user hasn't manually reordered
      if (!hasLoadedRef.current && !manuallyReordered) {
        console.log('✅ Loading data (first time for this date)');
        // Check if we already have data from parent (instant load)
        if (allPrayerTimes && Object.keys(allPrayerTimes).length > 0) {
          loadFromPropData();
        } else {
          // Fallback to fetching if no prop data (slower)
          loadAgendaItems();
        }
        hasLoadedRef.current = true;
      } else {
        console.log('⏭️ Skipping reload - already loaded for this date or manually reordered');
      }
    } else if (!isOpen) {
      // Reset when panel closes to prepare for next open
      console.log('🧹 Panel closed, resetting state (but preserving flags for handleClose)');
      hasLoadedRef.current = false;
      setAgendaItems([]);
      setError('');
      // DON'T reset manuallyReordered or hasChanges here - let handleClose do it
    }
  }, [isOpen, groupId, selectedDate]); // Only watch open/close and date changes

  // Fast load from prop data (instant)
  const loadFromPropData = () => {
    try {
      setIsLoading(true);
      
      const date = new Date(selectedDate);
      const dateStr = date.toISOString().split('T')[0];
      
      console.log(`⚡ Fast loading from prop data for ${dateStr}`);
      
      // Flatten all prayer times into a single list
      const allItems = [
        ...(allPrayerTimes.shacharit || []),
        ...(allPrayerTimes.mincha || []),
        ...(allPrayerTimes.maariv || [])
      ];
      
      console.log(`⚡ Found ${allItems.length} items from props`);
      
      const items = allItems.map(item => {
        const timeParts = parseTime12Hour(item.time && item.time !== 'No time' ? item.time : null);
        return {
          id: item.id,
          timeHour: timeParts.hour,
          timeMinute: timeParts.minute,
          timePeriod: timeParts.period,
          title: item.title || item.description || '', // Support both new and old fields
          details: item.details || item.notes || '',
          notes: '',
          recurringDays: item.recurring_days || [], // Use actual recurring days from API
          isDateSpecific: item.isOverride || false,
          applyToAllDays: !item.isOverride,
          displayOrder: item.display_order ?? 999, // Preserve display_order from backend
          isEditing: false,
          isNew: false,
          // Zmanim rule fields with defaults - map from API snake_case
          useZmanimRule: item.zmanim_rule_enabled || false,
          zmanimType: item.zmanim_type || 'sunset',
          zmanimOffsetMinutes: item.zmanim_offset_minutes || 0,
          zmanimOffsetDirection: item.zmanim_offset_direction || 'before',
          zmanimLocationInput: item.zmanim_location_zipcode || item.zmanim_location_input || '',
          zmanimCityName: item.zmanim_city_name || '',
          appliedZmanimRule: item.applied_zmanim_rule || ''
        };
      });
      
      // Sort by display_order first, then by time
      const sortedItems = items.sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        return sortItemsByTimeParts([a, b]).indexOf(a) === 0 ? -1 : 1;
      });
      
      console.log('⚡ Setting agenda items:', sortedItems.map(i => `${i.title} (order: ${i.displayOrder})`));
      setAgendaItems(sortedItems);
      setError('');
    } catch (error) {
      console.error('Error loading from prop data:', error);
      setError('Failed to load agenda items');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAgendaItems = async () => {
    try {
      setIsLoading(true);
      
      // Get the day index and date string for the selected date
      const date = new Date(selectedDate);
      const dayIndex = date.getDay();
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      console.log(`📋 Loading agenda items for ${dateStr} (day index: ${dayIndex})`);
      
      // Get date-specific overrides for this exact date
      const overridesResponse = await prayerService.getAgendaOverrides(groupId, dateStr);
      const dateOverrides = overridesResponse.data || [];
      
      console.log(`📋 Found ${dateOverrides.length} overrides for ${dateStr}:`, dateOverrides);
      
      // If there are overrides for this date, show ONLY overrides
      if (dateOverrides.length > 0) {
        const items = dateOverrides.map(item => {
          const timeParts = parseTime12Hour(item.time);
          const zmanimFields = mapZmanimFields(item);
          
          return {
            id: item.id,
            timeHour: timeParts.hour,
            timeMinute: timeParts.minute,
            timePeriod: timeParts.period,
            title: item.title || item.description || '', // Support both new and old fields
            details: item.details || item.notes || '',
            notes: item.notes || '',
            specificDate: item.specific_date,
            recurringDays: item.recurring_days || [], // Use actual recurring days from API
            isDateSpecific: true,
            applyToAllDays: false,
            displayOrder: item.display_order ?? 999, // Preserve display_order from backend
            isEditing: false,
            isNew: false,
            ...zmanimFields
          };
        });
        
        // Sort by display_order first, then by time
        const sortedItems = items.sort((a, b) => {
          if (a.displayOrder !== b.displayOrder) {
            return a.displayOrder - b.displayOrder;
          }
          return sortItemsByTimeParts([a, b]).indexOf(a) === 0 ? -1 : 1;
        });
        console.log(`📋 Setting ${sortedItems.length} override items in state`);
        setAgendaItems(sortedItems);
      } else {
        // No overrides - get recurring items that apply to this day
        // Use the same endpoint as week view to get properly filtered items
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
        const startDate = weekStart.toISOString().split('T')[0];
        const endDate = dateStr; // Just need this one day
        
        console.log(`📋 Fetching prayer times from ${startDate} to ${endDate}`);
        const prayerTimesResponse = await prayerService.getPrayerTimes(groupId, startDate, endDate);
        const schedules = prayerTimesResponse.data || {};
        
        // Get items for the selected day
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayIndex === 6 ? 'Shabbat' : dayNames[dayIndex];
        const daySchedule = schedules[dayName] || { shacharit: [], mincha: [], maariv: [] };
        
        console.log(`📋 Day schedule for ${dayName}:`, daySchedule);
        
        // Flatten all prayer times into a single list
        const allItems = [
          ...daySchedule.shacharit,
          ...daySchedule.mincha,
          ...daySchedule.maariv
        ];
        
        console.log(`📋 Found ${allItems.length} items for this day (after exclusions)`);
        
        const items = allItems.map(item => {
          const timeParts = parseTime12Hour(item.time && item.time !== 'No time' ? item.time : null);
          const zmanimFields = mapZmanimFields(item);
          
          return {
            id: item.id,
            timeHour: timeParts.hour,
            timeMinute: timeParts.minute,
            timePeriod: timeParts.period,
            title: item.title || item.description || '', // Support both new and old fields
            details: item.details || item.notes || '',
            notes: '',
            recurringDays: item.recurring_days || [], // Use actual recurring days from API
            isDateSpecific: item.isOverride || false,
            applyToAllDays: !item.isOverride,
            displayOrder: item.display_order ?? 999, // Preserve display_order from backend
            isEditing: false,
            isNew: false,
            ...zmanimFields
          };
        });
        
        // Sort by display_order first, then by time
        const sortedItems = items.sort((a, b) => {
          if (a.displayOrder !== b.displayOrder) {
            return a.displayOrder - b.displayOrder;
          }
          return sortItemsByTimeParts([a, b]).indexOf(a) === 0 ? -1 : 1;
        });
        console.log(`📋 Setting ${sortedItems.length} items in state`);
        setAgendaItems(sortedItems);
      }
      
      setError('');
    } catch (error) {
      console.error('Error loading agenda items:', error);
      setError('Failed to load agenda items');
    } finally {
      setIsLoading(false);
    }
  };

  // Add new item
  const handleAddItem = () => {
    // Get the day index for the selected date (0=Sunday, 6=Saturday)
    const date = new Date(selectedDate);
    const dayIndex = date.getDay();
    
    const newItem = {
      id: `temp_${Date.now()}`,
      timeHour: '',
      timeMinute: '',
      timePeriod: 'AM',
      title: '',
      details: '',
      recurringDays: [dayIndex], // Pre-select current day
      applyToAllDays: false, // New field to track if user wants recurring
      isEditing: true,
      isNew: true,
      // Zmanim rule fields
      useZmanimRule: false,
      zmanimType: 'sunset',
      zmanimOffsetMinutes: 0,
      zmanimOffsetDirection: 'before',
      zmanimLocationInput: ''
    };
    setAgendaItems(prev => [...prev, newItem]);
  };

  // Update item field
  const updateItem = (index, field, value) => {
    setAgendaItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Delete item
  const handleDeleteItem = (index) => {
    const item = agendaItems[index];
    
    if (item.isNew) {
      // Just remove from list if not saved yet
      setAgendaItems(prev => prev.filter((_, i) => i !== index));
      return;
    }

    // Show confirmation modal
    setDeleteOption('thisDay'); // Reset to default
    setDeleteConfirm({ show: true, index, item });
  };

  // Confirm delete
  const confirmDelete = async () => {
    const { index, item } = deleteConfirm;
    setDeleteConfirm({ show: false, index: null, item: null });
    
    console.log(`🗑️ Deleting item:`, item, 'Option:', deleteOption);
    
    // Optimistically remove from UI immediately
    setAgendaItems(prev => prev.filter((_, i) => i !== index));
    setError('');

    try {
      // Check if user wants to remove only from this day or all days
      const isRecurring = !item.isDateSpecific && item.recurringDays && item.recurringDays.length > 0;
      
      if (isRecurring && deleteOption === 'thisDay') {
        // Create an exclusion for this specific date
        const dateStr = new Date(selectedDate).toISOString().split('T')[0];
        await prayerService.createAgendaExclusion(groupId, {
          agenda_item_id: item.id,
          excluded_date: dateStr
        });
        console.log(`🚫 Created exclusion for item ${item.id} on ${dateStr}`);
      } else {
        // Delete the item entirely (works for both recurring and date-specific)
        if (item.isDateSpecific) {
          await prayerService.deleteAgendaOverride(groupId, item.id);
        } else {
          await prayerService.deleteAgendaItem(groupId, item.id);
        }
      }
      
      // Mark that changes were made
      setHasChanges(true);
      
      // Update the week view in background
      onPrayerTimesUpdate().catch(err => console.error('Error updating week view:', err));
    } catch (error) {
      console.error('🗑️ Error deleting agenda item:', error);
      setError(error.response?.data?.message || 'Failed to delete item');
      // Reload on error to restore correct state
      await loadAgendaItems();
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirm({ show: false, index: null, item: null });
  };

  // Start editing
  const handleEditItem = (index) => {
    setAgendaItems(prev => {
      const updated = [...prev];
      const item = updated[index];
      
      // Determine if this is currently a recurring item (has recurring_days)
      const isRecurring = item.recurringDays && item.recurringDays.length > 0;
      
      updated[index] = {
        ...item,
        applyToAllDays: isRecurring, // Set based on whether it's currently recurring
        isEditing: true
      };
      return updated;
    });
  };

  // Save item
  const handleSaveItem = async (index) => {
    const item = agendaItems[index];
    
    if (!item.title || !item.title.trim()) {
      setError('Title is required');
      return;
    }

    if (item.title.trim().length > 100) {
      setError('Title must be 100 characters or less');
      return;
    }

    // Show confirmation if zmanim rule is enabled
    if (item.useZmanimRule && !saveConfirm.show) {
      setSaveConfirm({ show: true, index });
      return;
    }

    // Reset confirmation state
    setSaveConfirm({ show: false, index: null });

    setIsLoading(true);
    setError('');

    try {
      // Check if this should be a date-specific override or recurring item
      const isDateSpecific = !item.applyToAllDays || (item.recurringDays || []).length === 0;
      
      // Format time string from parts (use "No time" if empty since DB requires a value)
      const timeString = formatTime12Hour(item.timeHour, item.timeMinute, item.timePeriod) || 'No time';
      
      if (isDateSpecific) {
        // Save as date-specific override
        const date = new Date(selectedDate);
        const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        const overrideData = {
          specific_date: dateStr,
          title: item.title.trim(),
          details: item.details && item.details.trim() ? item.details.trim() : null,
          time: timeString,
          notes: item.notes || ''
        };

        // Add zmanim rule if enabled
        if (item.useZmanimRule) {
          // Parse location input - could be ZIP code or GeoNames ID
          const locationInput = (item.zmanimLocationInput || '').trim();
          const isZipCode = /^\d{5}$/.test(locationInput);
          const isGeoId = /^\d+$/.test(locationInput) && locationInput.length > 5;
          
          overrideData.zmanimRule = {
            enabled: true,
            zmanimType: item.zmanimType || 'sunset',
            offsetMinutes: parseInt(item.zmanimOffsetMinutes) || 0,
            offsetDirection: item.zmanimOffsetDirection || 'before',
            location: {}
          };

          if (isZipCode) {
            overrideData.zmanimRule.location.zipcode = locationInput;
          } else if (isGeoId) {
            overrideData.zmanimRule.location.geonameid = parseInt(locationInput);
          } else if (locationInput) {
            // Try to parse as lat,lng if it contains a comma
            const parts = locationInput.split(',').map(p => p.trim());
            if (parts.length === 2) {
              const lat = parseFloat(parts[0]);
              const lng = parseFloat(parts[1]);
              if (!isNaN(lat) && !isNaN(lng)) {
                overrideData.zmanimRule.location.latitude = lat;
                overrideData.zmanimRule.location.longitude = lng;
              }
            }
          }
        }

        // Always CREATE override if:
        // 1. It's a new item (isNew=true), OR
        // 2. It was previously a recurring item (has recurringDays) being converted to override
        // Only UPDATE if it's an existing override (isDateSpecific=true)
        if (item.isNew || !item.isDateSpecific) {
          // If converting a recurring item to override, pass the original ID
          if (!item.isNew && !item.isDateSpecific && item.id) {
            overrideData.original_agenda_item_id = item.id;
            console.log('🔗 Converting recurring item to override, will create exclusion for:', item.id);
          }
          
          // CREATE new override
          console.log('💾 Creating new override:', overrideData);
          const response = await prayerService.createAgendaOverride(groupId, overrideData);
          const timeParts = parseTime12Hour(response.data.time);
          const zmanimFields = mapZmanimFields(response.data);
          const newItem = {
            ...response.data,
            timeHour: timeParts.hour,
            timeMinute: timeParts.minute,
            timePeriod: timeParts.period,
            title: response.data.title || response.data.description || '',
            details: response.data.details || response.data.notes || '',
            isDateSpecific: true,
            applyToAllDays: false,
            isEditing: false,
            isNew: false,
            ...zmanimFields
          };
          
          console.log('✅ Created item, updating state:', newItem.title);
          setAgendaItems(prev => {
            const updated = [...prev];
            updated[index] = newItem;
            const sorted = sortItemsByTimeParts(updated);
            console.log('✅ Final items after save:', sorted.map(i => i.title));
            return sorted;
          });
        } else {
          // UPDATE existing override
          const response = await prayerService.updateAgendaOverride(groupId, item.id, overrideData);
          const timeParts = parseTime12Hour(response.data.time);
          const zmanimFields = mapZmanimFields(response.data);
          const updatedItem = {
            ...response.data,
            timeHour: timeParts.hour,
            timeMinute: timeParts.minute,
            timePeriod: timeParts.period,
            title: response.data.title || response.data.description || '',
            details: response.data.details || response.data.notes || '',
            isDateSpecific: true,
            applyToAllDays: false,
            isEditing: false,
            isNew: false,
            ...zmanimFields
          };
          
          setAgendaItems(prev => {
            const updated = [...prev];
            updated[index] = updatedItem;
            return sortItemsByTimeParts(updated);
          });
        }
      } else {
        // Save as recurring item
        const itemData = {
          title: item.title.trim(),
          details: item.details && item.details.trim() ? item.details.trim() : null,
          time: timeString,
          recurring_days: item.recurringDays || [],
          notes: item.notes || ''
        };

        // Add zmanim rule if enabled
        if (item.useZmanimRule) {
          // Parse location input - could be ZIP code or GeoNames ID
          const locationInput = (item.zmanimLocationInput || '').trim();
          const isZipCode = /^\d{5}$/.test(locationInput);
          const isGeoId = /^\d+$/.test(locationInput) && locationInput.length > 5;
          
          itemData.zmanimRule = {
            enabled: true,
            zmanimType: item.zmanimType || 'sunset',
            offsetMinutes: parseInt(item.zmanimOffsetMinutes) || 0,
            offsetDirection: item.zmanimOffsetDirection || 'before',
            location: {}
          };

          if (isZipCode) {
            itemData.zmanimRule.location.zipcode = locationInput;
          } else if (isGeoId) {
            itemData.zmanimRule.location.geonameid = parseInt(locationInput);
          } else if (locationInput) {
            // Try to parse as lat,lng if it contains a comma
            const parts = locationInput.split(',').map(p => p.trim());
            if (parts.length === 2) {
              const lat = parseFloat(parts[0]);
              const lng = parseFloat(parts[1]);
              if (!isNaN(lat) && !isNaN(lng)) {
                itemData.zmanimRule.location.latitude = lat;
                itemData.zmanimRule.location.longitude = lng;
              }
            }
          }
        }

        if (item.isNew) {
          // Create new item
          const response = await prayerService.createAgendaItem(groupId, itemData);
          const timeParts = parseTime12Hour(response.data.time);
          const zmanimFields = mapZmanimFields(response.data);
          const newItem = {
            ...response.data,
            timeHour: timeParts.hour,
            timeMinute: timeParts.minute,
            timePeriod: timeParts.period,
            title: response.data.title || response.data.description || '',
            details: response.data.details || response.data.notes || '',
            recurringDays: response.data.recurring_days || [],
            applyToAllDays: true,
            isEditing: false,
            isNew: false,
            ...zmanimFields
          };
          
          setAgendaItems(prev => {
            const updated = [...prev];
            updated[index] = newItem;
            return sortItemsByTimeParts(updated);
          });
        } else {
          // Update existing item
          const response = await prayerService.updateAgendaItem(groupId, item.id, itemData);
          const timeParts = parseTime12Hour(response.data.time);
          const zmanimFields = mapZmanimFields(response.data);
          const updatedItem = {
            ...response.data,
            timeHour: timeParts.hour,
            timeMinute: timeParts.minute,
            timePeriod: timeParts.period,
            title: response.data.title || response.data.description || '',
            details: response.data.details || response.data.notes || '',
            recurringDays: response.data.recurring_days || [],
            applyToAllDays: true,
            isEditing: false,
            isNew: false,
            ...zmanimFields
          };
          
          setAgendaItems(prev => {
            const updated = [...prev];
            updated[index] = updatedItem;
            return sortItemsByTimeParts(updated);
          });
        }
      }

      // Mark that changes were made
      setHasChanges(true);
      
      // Update the week view in background (non-blocking)
      onPrayerTimesUpdate().catch(err => console.error('Error updating week view:', err));
    } catch (error) {
      console.error('Error saving agenda item:', error);
      setError(error.response?.data?.message || 'Failed to save item');
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = (index) => {
    setAgendaItems(prev => {
      const item = prev[index];
      if (item.isNew) {
        return prev.filter((_, i) => i !== index);
      }
      const updated = [...prev];
      updated[index].isEditing = false;
      return updated;
    });
  };

  // Toggle recurring day
  const toggleRecurringDay = (index, dayIndex) => {
    console.log('Toggling day:', dayIndex, 'for item at index:', index);
    setAgendaItems(prev => {
      const updated = [...prev];
      const item = { ...updated[index] }; // Create a new item object
      const days = [...(item.recurringDays || [])]; // Create new array
      
      if (days.includes(dayIndex)) {
        item.recurringDays = days.filter(d => d !== dayIndex);
      } else {
        item.recurringDays = [...days, dayIndex].sort();
      }
      
      updated[index] = item; // Update the item in the array
      console.log('Updated recurring days:', item.recurringDays);
      return updated;
    });
  };

  // Calculate zmanim time from Hebcal API with validation
  const calculateZmanimTime = async (index) => {
    const item = agendaItems[index];
    
    // Validate ZIP code
    if (!item.zmanimLocationInput || item.zmanimLocationInput.trim().length !== 5) {
      setError('Please enter a valid 5-digit ZIP code');
      return;
    }

    try {
      // Set loading state
      setCalculatingZmanim(index);
      
      // Clear any previous errors and previews
      setError('');
      setZmanimPreview(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });

      const dateStr = new Date(selectedDate).toISOString().split('T')[0];
      const zipCode = item.zmanimLocationInput.trim();
      
      const params = new URLSearchParams({
        date: dateStr,
        zmanimType: item.zmanimType,
        offsetMinutes: item.zmanimOffsetMinutes || 0,
        offsetDirection: item.zmanimOffsetDirection,
        zipcode: zipCode
      });

      const response = await fetch(`/api/zmanim/preview?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to calculate time');
      }
      
      if (data.success && data.data.calculatedTime) {
        const calculatedTime = data.data.calculatedTime;
        const timeParts = parseTime12Hour(calculatedTime);
        const locationInfo = data.data.location;
        
        // Build location display text
        let locationText = '';
        if (locationInfo?.city && locationInfo?.tzid) {
          locationText = `${locationInfo.city} (${locationInfo.tzid})`;
        } else if (locationInfo?.title) {
          locationText = locationInfo.title;
        }
        
        // Update the item with the calculated time and rule description
        setAgendaItems(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            timeHour: timeParts.hour,
            timeMinute: timeParts.minute,
            timePeriod: timeParts.period,
            appliedZmanimRule: data.data.ruleDescription, // Store rule description
            zmanimCityName: locationText // Store location info
          };
          return updated;
        });

        // Clear any errors
        setError('');
      } else {
        throw new Error('Invalid response from zmanim API');
      }
    } catch (error) {
      console.error('Error calculating zmanim time:', error);
      setError(error.message || 'Failed to validate zmanim rule. Please check your ZIP code and try again.');
    } finally {
      // Clear loading state
      setCalculatingZmanim(null);
    }
  };

  // Handle close - update week view when panel closes
  const handleClose = async () => {
    console.log('🚪 Closing panel, checking for changes...', {
      manuallyReordered,
      hasChanges,
      willRefresh: (manuallyReordered || hasChanges) && onPrayerTimesUpdate
    });
    
    // If user made any changes (reorder, add, edit, delete), refresh the week view BEFORE closing
    if ((manuallyReordered || hasChanges) && onPrayerTimesUpdate) {
      console.log('🔄 Triggering week view refresh before close');
      try {
        await onPrayerTimesUpdate();
        console.log('✅ Week view refreshed successfully');
        // Show snackbar to indicate changes were saved
        console.log('📢 Calling showSnackbar...');
        showSnackbar("Schedule updated...refreshing");
        console.log('📢 showSnackbar called');
      } catch (err) {
        console.error('Error updating week view:', err);
      }
    } else {
      console.log('⏭️ No changes detected, skipping refresh and snackbar');
    }
    
    // Reset flags
    setManuallyReordered(false);
    setHasChanges(false);
    
    // Call parent's onClose after refresh completes
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity duration-300"
          onClick={handleClose}
        />
      )}

      {/* Slide-in Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-full md:w-[500px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0 animate-slide-in-from-right' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{formatDisplayDate()}</h2>
            <p className="text-sm text-gray-500">Manage today's schedule</p>
            {holidays && holidays.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {holidays.map((holiday, idx) => (
                  <span 
                    key={idx}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {holiday.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-3">
            {agendaItems.map((item, index) => (
              <div 
                key={item.id}
                className="relative"
              >
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all">
                {item.isEditing ? (
                  // Edit Mode
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        maxLength="100"
                        value={item.title || ''}
                        onChange={(e) => updateItem(index, 'title', e.target.value)}
                        placeholder="e.g., Shacharit, Kiddush, Learning"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">{(item.title || '').length}/100 characters</p>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Details (optional)</label>
                      <textarea
                        rows="2"
                        value={item.details || ''}
                        onChange={(e) => updateItem(index, 'details', e.target.value)}
                        placeholder="Additional information, notes, announcements..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                      />
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-gray-700">Time (optional)</label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs font-medium text-gray-700">Apply a Rule</span>
                          <div className="relative inline-block w-9 h-5">
                            <input
                              type="checkbox"
                              checked={item.useZmanimRule || false}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                updateItem(index, 'useZmanimRule', isChecked);
                                
                                // If turning OFF the rule, clear the applied rule and reset time fields
                                if (!isChecked) {
                                  setAgendaItems(prev => {
                                    const updated = [...prev];
                                    updated[index] = {
                                      ...updated[index],
                                      appliedZmanimRule: '',
                                      timeHour: '',
                                      timeMinute: '',
                                      timePeriod: 'AM'
                                    };
                                    return updated;
                                  });
                                }
                              }}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                          </div>
                        </label>
                      </div>
                      <div className={`flex gap-2 items-center ${item.appliedZmanimRule ? 'ring-2 ring-blue-500 rounded-md p-2 bg-blue-50' : ''}`}>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength="2"
                          value={item.timeHour || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, ''); // Only digits
                            if (val === '') {
                              updateItem(index, 'timeHour', '');
                            } else {
                              const num = parseInt(val, 10);
                              if (num >= 1 && num <= 12) {
                                updateItem(index, 'timeHour', val);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            // Pad on blur only if there's a value
                            const val = e.target.value;
                            if (val && val.length === 1) {
                              updateItem(index, 'timeHour', val.padStart(2, '0'));
                            }
                          }}
                          placeholder="HH"
                          className="w-14 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-center"
                          disabled={item.useZmanimRule}
                        />
                        <span className="text-gray-500 font-medium">:</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength="2"
                          value={item.timeMinute || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, ''); // Only digits
                            if (val === '') {
                              updateItem(index, 'timeMinute', '');
                            } else {
                              const num = parseInt(val, 10);
                              if (num >= 0 && num <= 59) {
                                updateItem(index, 'timeMinute', val);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            // Pad on blur only if there's a value
                            const val = e.target.value;
                            if (val && val.length === 1) {
                              updateItem(index, 'timeMinute', val.padStart(2, '0'));
                            }
                          }}
                          placeholder="MM"
                          className="w-14 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-center"
                          disabled={item.useZmanimRule}
                        />
                        <select
                          value={item.timePeriod || 'AM'}
                          onChange={(e) => updateItem(index, 'timePeriod', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                          disabled={item.useZmanimRule}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      {item.appliedZmanimRule && (
                        <p className="text-xs text-blue-700 mt-1 font-medium">
                          {item.appliedZmanimRule}
                        </p>
                      )}
                    </div>

                    {/* Simplified Zmanim Rule Configuration */}
                    {item.useZmanimRule && (
                      <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                        {/* Zman Type Dropdown */}
                        <div className="relative">
                          <select
                            value={item.zmanimType || 'sunset'}
                            onChange={(e) => updateItem(index, 'zmanimType', e.target.value)}
                            className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white font-medium appearance-none cursor-pointer"
                          >
                            <option value="sunrise">Sunrise</option>
                            <option value="sunset">Sunset</option>
                            <option value="sofZmanShma">Latest Shema</option>
                            <option value="sofZmanTfilla">Latest Shacharit</option>
                            <option value="chatzot">Midday</option>
                            <option value="minchaGedola">Mincha Gedola</option>
                            <option value="minchaKetana">Mincha Ketana</option>
                            <option value="plagHaMincha">Plag HaMincha</option>
                            <option value="alotHaShachar">Alot HaShachar</option>
                            <option value="misheyakir">Misheyakir</option>
                            <option value="tzeit7083deg">Tzeit (3 stars)</option>
                            <option value="tzeit72min">Tzeit (Rabbeinu Tam)</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>

                        {/* Minutes + Rule Type (Before/After) - SWAPPED ORDER */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={item.zmanimOffsetMinutes || 0}
                              onChange={(e) => updateItem(index, 'zmanimOffsetMinutes', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2.5 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium"
                              placeholder="0"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">min</span>
                          </div>
                          <div className="relative">
                            <select
                              value={item.zmanimOffsetDirection || 'before'}
                              onChange={(e) => updateItem(index, 'zmanimOffsetDirection', e.target.value)}
                              className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white font-medium appearance-none cursor-pointer"
                            >
                              <option value="before">Before</option>
                              <option value="after">After</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        </div>

                        {/* ZIP Code Input with Lookup */}
                        <div>
                          <input
                            type="text"
                            value={item.zmanimLocationInput || ''}
                            onBlur={async (e) => {
                              const value = e.target.value;
                              // Auto-lookup when 5 digits entered
                              if (value.length === 5) {
                                try {
                                  const dateStr = new Date(selectedDate).toISOString().split('T')[0];
                                  const response = await fetch(`/api/zmanim?date=${dateStr}&zipcode=${value}`, {
                                    headers: {
                                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    }
                                  });
                                  const data = await response.json();
                                  if (data.location && data.location.name) {
                                    updateItem(index, 'zmanimCityName', data.location.name);
                                  }
                                } catch (err) {
                                  console.error('ZIP lookup failed:', err);
                                }
                              }
                            }}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                              updateItem(index, 'zmanimLocationInput', value);
                              
                              // Clear city name when user types
                              if (item.zmanimCityName) {
                                updateItem(index, 'zmanimCityName', '');
                              }
                            }}
                            placeholder="ZIP code"
                            maxLength="5"
                            className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium ${
                              item.zmanimCityName
                                ? 'border-green-300 bg-green-50'
                                : item.zmanimLocationInput && item.zmanimLocationInput.length === 5
                                ? 'border-gray-300 bg-white'
                                : 'border-gray-300 bg-white'
                            }`}
                          />
                          {item.zmanimCityName && (
                            <p className="text-xs text-green-700 mt-1 font-medium flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              {item.zmanimCityName}
                            </p>
                          )}
                          {item.zmanimLocationInput && item.zmanimLocationInput.length !== 5 && (
                            <p className="text-xs text-red-600 mt-1">ZIP code must be 5 digits</p>
                          )}
                        </div>

                        {/* Test/Apply Button */}
                        <button
                          type="button"
                          onClick={() => calculateZmanimTime(index)}
                          disabled={!item.zmanimLocationInput || item.zmanimLocationInput.length !== 5 || calculatingZmanim === index}
                          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                        >
                          {calculatingZmanim === index ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                              Calculating...
                            </>
                          ) : (
                            'Apply'
                          )}
                        </button>

                        {/* Error Message */}
                        {error && item.isEditing && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              <p className="text-sm text-red-700 flex-1">{error}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Apply to All Days Toggle */}
                    <div className="border-t border-gray-200 pt-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.applyToAllDays || false}
                          onChange={(e) => {
                            updateItem(index, 'applyToAllDays', e.target.checked);
                            // If turning off, clear recurring days except current day
                            if (!e.target.checked) {
                              const date = new Date(selectedDate);
                              const dayIndex = date.getDay();
                              updateItem(index, 'recurringDays', [dayIndex]);
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Apply to multiple days</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        {item.applyToAllDays 
                          ? 'Select which days this item should appear on' 
                          : 'This item will only appear on the selected day'}
                      </p>
                    </div>
                    
                    {/* Recurring Days Selector - Only show if Apply to All Days is checked */}
                    {item.applyToAllDays && (
                      <div>
                        <div className="block text-xs font-medium text-gray-700 mb-2">Select Days</div>
                        <div className="flex gap-2">
                          {daysOfWeek.map((day, dayIndex) => (
                            <button
                              key={dayIndex}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleRecurringDay(index, dayIndex);
                              }}
                              className={`w-8 h-8 rounded-full text-xs font-medium transition-all cursor-pointer ${
                                (item.recurringDays || []).includes(dayIndex)
                                  ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                              title={dayNames[dayIndex]}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.recurringDays && item.recurringDays.length > 0
                            ? `Will appear on ${item.recurringDays.map(d => dayNames[d]).join(', ')}`
                            : 'Select at least one day'}
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleSaveItem(index)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => handleCancelEdit(index)}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      {/* Move Up/Down Buttons */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className={`p-1 rounded transition-colors ${
                            index === 0 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                          title="Move up"
                        >
                          <FontAwesomeIcon icon={faChevronUp} className="text-xs" />
                        </button>
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === agendaItems.length - 1}
                          className={`p-1 rounded transition-colors ${
                            index === agendaItems.length - 1
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                          title="Move down"
                        >
                          <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                        </button>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">
                          {item.title}
                        </h3>
                        {item.details && (
                          <p className="text-xs text-gray-600 mb-1.5 line-clamp-2">
                            {item.details}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faClock} className="text-xs" />
                            {item.useZmanimRule && '~ '}
                            {formatTime12Hour(item.timeHour, item.timeMinute, item.timePeriod) || 'No time'}
                          </span>
                          {item.recurringDays && item.recurringDays.length > 0 && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <FontAwesomeIcon icon={faRepeat} className="text-xs" />
                              {item.recurringDays.map(d => daysOfWeek[d]).join(', ')}
                            </span>
                          )}
                        </div>
                        {/* Zmanim Rule Display */}
                        {item.useZmanimRule && (
                          <div className="mt-1 text-xs text-purple-600 font-medium">
                            {(item.zmanimOffsetMinutes || 0) > 0 ? `${item.zmanimOffsetMinutes} min ` : ''}
                            {item.zmanimOffsetDirection || 'before'}{' '}
                            {(() => {
                              const typeMap = {
                                'sunrise': 'Sunrise',
                                'sunset': 'Sunset',
                                'sofZmanShma': 'Latest Shema',
                                'sofZmanTfilla': 'Latest Shacharit',
                                'chatzot': 'Midday',
                                'minchaGedola': 'Mincha Gedola',
                                'minchaKetana': 'Mincha Ketana',
                                'plagHaMincha': 'Plag HaMincha',
                                'alotHaShachar': 'Alot HaShachar',
                                'misheyakir': 'Misheyakir',
                                'tzeit7083deg': 'Tzeit (3 stars)',
                                'tzeit72min': 'Tzeit (Rabbeinu Tam)'
                              };
                              return typeMap[item.zmanimType] || item.zmanimType || 'sunset';
                            })()}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditItem(index)}
                          className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faPencil} className="text-sm" />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(index)}
                          className="text-gray-400 hover:text-red-600 transition-colors p-1"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            ))}

            {/* Add Item Button */}
            <button
              onClick={handleAddItem}
              className="w-full border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-600 hover:text-blue-600 px-4 py-4 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} className="text-sm" />
              <span className="text-sm font-medium">Add Item</span>
            </button>
          </div>
        </div>
      </div>

      {/* Save Confirmation Modal for Zmanim Rules */}
      {saveConfirm.show && saveConfirm.index !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faClock} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Apply Zmanim Rule?
                </h3>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-1">
                {agendaItems[saveConfirm.index]?.title}
              </p>
              <p className="text-sm text-blue-700">
                {agendaItems[saveConfirm.index]?.zmanimOffsetMinutes > 0 
                  ? `${agendaItems[saveConfirm.index]?.zmanimOffsetMinutes} min ` 
                  : ''}
                {agendaItems[saveConfirm.index]?.zmanimOffsetMinutes > 0 
                  ? agendaItems[saveConfirm.index]?.zmanimOffsetDirection + ' ' 
                  : ''}
                {(() => {
                  const zmanimType = agendaItems[saveConfirm.index]?.zmanimType;
                  const typeMap = {
                    'sunrise': 'Sunrise',
                    'sunset': 'Sunset',
                    'sofZmanShma': 'Latest Shema',
                    'sofZmanTfilla': 'Latest Shacharit',
                    'chatzot': 'Midday',
                    'minchaGedola': 'Mincha Gedola',
                    'minchaKetana': 'Mincha Ketana',
                    'plagHaMincha': 'Plag HaMincha',
                    'alotHaShachar': 'Alot HaShachar',
                    'misheyakir': 'Misheyakir',
                    'tzeit7083deg': 'Tzeit (3 stars)',
                    'tzeit72min': 'Tzeit (Rabbeinu Tam)'
                  };
                  return typeMap[zmanimType] || zmanimType || 'Sunset';
                })()}
              </p>
            </div>

            {agendaItems[saveConfirm.index]?.applyToAllDays && 
             agendaItems[saveConfirm.index]?.recurringDays?.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  Applies to: {agendaItems[saveConfirm.index].recurringDays.map(d => dayNames[d]).join(', ')}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setSaveConfirm({ show: false, index: null })}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveItem(saveConfirm.index)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Apply Rule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              How should this item be removed?
            </h3>
            
            {/* Show item title */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Item to remove:</p>
              <p className="font-medium text-gray-900">"{deleteConfirm.item?.title}"</p>
            </div>

            {/* Check if it's a recurring item */}
            {!deleteConfirm.item?.isDateSpecific && deleteConfirm.item?.recurringDays?.length > 0 ? (
              <>
                {/* Show recurring days */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FontAwesomeIcon icon={faRepeat} className="text-blue-600" />
                    <p className="text-sm font-medium text-blue-900">This is a recurring item</p>
                  </div>
                  <p className="text-xs text-blue-700">
                    Appears on: {deleteConfirm.item.recurringDays.map(d => dayNames[d]).join(', ')}
                  </p>
                </div>

                {/* Radio button options */}
                <div className="space-y-3 mb-6">
                  <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                         style={{ borderColor: deleteOption === 'thisDay' ? '#3b82f6' : '#e5e7eb' }}>
                    <input
                      type="radio"
                      name="deleteOption"
                      value="thisDay"
                      checked={deleteOption === 'thisDay'}
                      onChange={(e) => setDeleteOption(e.target.value)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Remove only from {formatDisplayDate()}</div>
                      <div className="text-sm text-gray-600">Item will still appear on other days</div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                         style={{ borderColor: deleteOption === 'allDays' ? '#3b82f6' : '#e5e7eb' }}>
                    <input
                      type="radio"
                      name="deleteOption"
                      value="allDays"
                      checked={deleteOption === 'allDays'}
                      onChange={(e) => setDeleteOption(e.target.value)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Remove from all recurring days</div>
                      <div className="text-sm text-gray-600">Item will be permanently deleted</div>
                    </div>
                  </label>
                </div>
              </>
            ) : (
              /* Simple confirmation for date-specific items */
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this item? This action cannot be undone.
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                {deleteOption === 'thisDay' && !deleteConfirm.item?.isDateSpecific ? 'Remove from This Day' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DayAgendaPanel;
